import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiFetch from "../utils/apiFetch";
import { parseCoordinate } from "../utils/coords";
import useDebounce from "../hooks/useDebounce";
import SchemeModal from "../components/schemeModal/SchemeModal";
import UserContext from "../hooks/UserContext";

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";

const LIMIT = 30;

// ─── Check items ──────────────────────────────────────────────────────────────
const CHECK_ITEMS = [
  ["foundation_cond", "Tayanch fundamenti holati"],
  ["foundation_nuts_tight", "Tayanch fundamentidagi gaykalar to'liq qotirilganligi"],
  ["base_washout", "Tayanch ostini suv yuvish, ochilib qolish holati"],
  ["soil_protect_obval", "Tayanch osti, fundamentlari tuproq bilan himoyalanganligi (obvalovka)"],
  ["earthing_kz_cond", "Tayanch yerlagichlari holati (KZ)"],
  ["concrete_base_protect", "J/B tayanch holati, ostki qismi tuproq bilan himoyalanganligi"],
  ["pole_tilt", "Tayanchni qiyshaygan, og'ishma holati"],
  ["dispatcher_marks", "Dispetcherlik yozuvlari, nomlanishi mavjudligi"],
  ["angle_iron_cond", "Tayanch ugolniklari holati"],
  ["nuts_welded", "Tayanch gaykalarining payvandlanganligi"],
  ["bolts_thread_punched", "Tayanchlardagi boltlarning rezbalari kerno qilinganligi"],
  ["angle_iron_welded", "Tayanchlardagi ugolniklarni bir-biriga payvandlanganligi"],
  ["factory_welds_cond", "Ishlab chiqaruvchi tomonidan payvandlangan choklarining holati"],
  ["leg_fit_to_found", "Metall tayanch oyog'ining fundament bilan oralig'i jipsligi"],
  ["guy_anbolts_cond", "Tayanch tortqichlari va AN boltlarini holati"],
  ["cotter_pins", "Shplintlar mavjudligi"],
  ["insulators_cond", "Izolyatorlar holati va ularning to'g'riligi, og'ishma holati"],
  ["conductors_cond", "Simlarni holati"],
  ["shunt_cond", "Shunt holati"],
  ["sas_present", "Shleyflardagi hamda tayanchlar oralig'ida SASlar mavjudligi"],
  ["thermovision_temp", "Boltli va pressli birikmalardagi teplovizor nazoratida harorat ko'rsatgichi"],
  ["temp_sas_c", "САС (С°)"],
  ["temp_nas_c", "НАС (С°)"],
  ["pa_clamps_cond", "Boltli birikmalar (PA qisqichlar) holati"],
  ["thermoweld_present", "Termopayka mavjudligi"],
  ["damper_gvn_cond", "Simdagi so'ndirg'ich holati (GVN)"],
  ["spacers_cond", "Rasporkalar holati"],
  ["gzt_present", "Chaqmoqqa qarshi tros (GZT) mavjudligi"],
  ["gzt_cond", "Chaqmoqqa qarshi tros holati"],
  ["spark_gap_cond", "Uchqun oraliq holati (Искровой промежуток)"],
  ["gzt_earthing_cond", "Chaqmoqqa qarshi tros yerlanganlik holati"],
  ["vols_cond", "Optik tolali aloqa tarmog'i (VOLS) holati"],
  ["corridor_trees", "Muhofaza hududida daraxtlar mavjudligi"],
  ["corridor_reeds", "Muhofaza hududida qamish"],
  ["corridor_wheat", "Muhofaza hududida bug'doy maydonlari mavjudligi"],
  ["corridor_buildings", "Muhofaza hududda bino, inshoatlar borligi"],
  ["clearance_ok", "Sim bilan yer, daraxt, bino orasidagi masofa holati"],
  ["bird_guard_qqt", "QQT (qushlarga qarshi to'siq) mavjudligi"],
  ["cross_road", "Yo'l bilan kesishmalar mavjudligi"],
  ["cross_powerline", "Elektr tarmog'i bilan kesishmalar mavjudligi"],
  ["cross_river", "Daryo va hokazo bilan kesishmalar mavjudligi"],
  ["winter_ice", "Qish mavsumida sim, trosdagi muzlamalar mavjudligi"],
];

const SHORT_CHECK_KEYS = new Set([
  "base_washout", "soil_protect_obval", "pole_tilt", "dispatcher_marks",
  "angle_iron_cond", "insulators_cond", "conductors_cond", "shunt_cond",
  "sas_present", "pa_clamps_cond", "damper_gvn_cond", "gzt_cond",
  "vols_cond", "corridor_trees", "corridor_buildings", "clearance_ok",
  "bird_guard_qqt", "cross_road", "cross_powerline", "cross_river",
]);

const isShortCheckup = (t) => String(t || "").toLowerCase() === "short";
const getVisibleItems = (t) =>
  isShortCheckup(t) ? CHECK_ITEMS.filter(([k]) => SHORT_CHECK_KEYS.has(k)) : CHECK_ITEMS;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getCurrentYearRange = () => {
  const year = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
  }).format(new Date());
  return { from: `${year}-01-01`, to: `${year}-12-31` };
};

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return new Intl.DateTimeFormat("uz-UZ", {
    timeZone: "Asia/Tashkent",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d);
};

const STATUS_LABEL = {
  verified: "Тасдиқланди",
  failed: "Мувофиқ эмас",
  pending: "Текширилмаган",
  skipped_offline: "Офлайн",
};
const STATUS_MOD = {
  verified: "verified",
  failed: "failed",
  pending: "pending",
  skipped_offline: "offline",
};

const REASON_LABEL = {
  accuracy_bad: "GPS аниқлиги паст",
  too_far: "Таяначдан узоқ",
  no_gps: "Геолокация йўқ",
  no_pole_coords: "Таянач координаталари йўқ",
  verify_api_error: "Текширув хизмати хатоси",
};

const safeName = (u) => {
  if (!u) return "";
  return `${u.lastname || ""} ${u.name || ""}`.trim();
};

const parseNegReasons = (x) => {
  if (!x) return {};
  if (typeof x === "object") return x;
  try { return JSON.parse(x); } catch { return {}; }
};

function countIssues(raw, checkupType) {
  const keys = isShortCheckup(checkupType)
    ? Array.from(SHORT_CHECK_KEYS)
    : CHECK_ITEMS.map(([k]) => k);
  let no = 0;
  for (const k of keys) {
    if (String(raw?.[k] ?? "").toLowerCase() === "no") no++;
  }
  return no;
}

// ─── Pagination helper ────────────────────────────────────────────────────────
function pageButtons(current, total) {
  if (total <= 1) return [];
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  for (let i of range) {
    if (l) {
      if (i - l === 2) rangeWithDots.push(l + 1);
      else if (i - l !== 1) rangeWithDots.push("...");
    }
    rangeWithDots.push(i);
    l = i;
  }
  return rangeWithDots;
}

// ─── Coverage Stats Panel ─────────────────────────────────────────────────────
function getCheckupRowId(item) {
  return item?.id ?? item?.client_id ?? `${item?.pole_id || "checkup"}-${item?.performed_at || item?.date_created || ""}`;
}

function buildCheckupsParams(p, filters) {
  const params = new URLSearchParams({ page: String(p) });
  if (filters.q) params.set("q", filters.q);
  if (filters.networkQ) params.set("network_q", filters.networkQ);
  if (filters.status) params.set("status", filters.status);
  if (filters.isOffline !== "") params.set("is_offline", filters.isOffline);
  if (filters.checkupType) params.set("checkup_type", filters.checkupType);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.branchId) params.set("branch_id", filters.branchId);
  if (filters.inspectorId) params.set("inspector_id", filters.inspectorId);
  if (filters.isEmergency) params.set("is_emergency", "true");
  return params;
}

const VOLT_LABELS = ["500", "220", "110", "35"];

function buildCoverageUrl(path, from, to) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function CoverageStats({ open, from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    setLoading(true);
    apiFetch(
      buildCoverageUrl("/api/pole-checkups/stats/coverage", from, to),
      { signal: controller.signal }
    )
      .then((r) => {
        if (!r.ok) throw new Error("Coverage stats request failed");
        return r.json();
      })
      .then((j) => setData(j.rows || []))
      .catch((error) => {
        if (error?.name !== "AbortError") setData([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, from, to]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiFetch(
        buildCoverageUrl("/api/pole-checkups/stats/coverage/export", from, to)
      );
      if (!res.ok) throw new Error("Coverage export request failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "checkups_coverage.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
    setExporting(false);
  };

  // Totals
  const totals = React.useMemo(() => {
    if (!data) return null;
    const t = { poles_total: 0, checkups_total: 0 };
    for (const v of VOLT_LABELS) {
      t[`poles_${v}`] = 0;
      t[`checkups_${v}`] = 0;
    }
    for (const r of data) {
      t.poles_total += r.poles_total || 0;
      t.checkups_total += r.checkups_total || 0;
      for (const v of VOLT_LABELS) {
        t[`poles_${v}`] += r[`poles_${v}`] || 0;
        t[`checkups_${v}`] += r[`checkups_${v}`] || 0;
      }
    }
    return t;
  }, [data]);

  const pct = (chk, pol) =>
    pol > 0 ? ((chk / pol) * 100).toFixed(1) + "%" : "—";

  const pctClass = (chk, pol) => {
    if (!pol) return "";
    const p = (chk / pol) * 100;
    if (p >= 80) return "ck-cov-good";
    if (p >= 40) return "ck-cov-mid";
    return "ck-cov-low";
  };

  if (!open) return null;

  return (
    <div className="ck-coverage">
      <div className="ck-coverage__body">
          {loading && (
            <div className="ck-state" style={{ padding: "24px 0" }}>
              <div className="ck-spinner" />
              Юкланмоқда...
            </div>
          )}
          {!loading && data && (
            <div className="ck-coverage__scroll">
              <table className="ck-cov-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="ck-cov-table__branch">Филиал</th>
                    <th colSpan={5} className="ck-cov-table__group">Опоралар</th>
                    <th colSpan={5} className="ck-cov-table__group ck-cov-table__group--chk">Кўриклар</th>
                    <th rowSpan={2} className="ck-cov-table__pct">%</th>
                  </tr>
                  <tr>
                    <th className="ck-cov-table__sub">Жами</th>
                    {VOLT_LABELS.map((v) => (
                      <th key={`ph${v}`} className="ck-cov-table__sub">{v}</th>
                    ))}
                    <th className="ck-cov-table__sub ck-cov-table__sub--chk">Жами</th>
                    {VOLT_LABELS.map((v) => (
                      <th key={`ch${v}`} className="ck-cov-table__sub ck-cov-table__sub--chk">{v}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((r) => (
                    <tr key={r.city_id}>
                      <td className="ck-cov-table__branch-cell">{r.city_name}</td>
                      <td className="ck-cov-table__num ck-cov-table__num--bold">{(r.poles_total || 0).toLocaleString()}</td>
                      {VOLT_LABELS.map((v) => (
                        <td key={`p${v}`} className="ck-cov-table__num">{(r[`poles_${v}`] || 0).toLocaleString()}</td>
                      ))}
                      <td className="ck-cov-table__num ck-cov-table__num--bold ck-cov-table__num--chk">{(r.checkups_total || 0).toLocaleString()}</td>
                      {VOLT_LABELS.map((v) => (
                        <td key={`c${v}`} className="ck-cov-table__num ck-cov-table__num--chk">{(r[`checkups_${v}`] || 0).toLocaleString()}</td>
                      ))}
                      <td className={"ck-cov-table__num ck-cov-table__num--pct " + pctClass(r.checkups_total, r.poles_total)}>
                        {pct(r.checkups_total, r.poles_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totals && data.length > 1 && (
                  <tfoot>
                    <tr>
                      <td className="ck-cov-table__branch-cell ck-cov-table__branch-cell--total">Жами</td>
                      <td className="ck-cov-table__num ck-cov-table__num--bold ck-cov-table__num--total">{totals.poles_total.toLocaleString()}</td>
                      {VOLT_LABELS.map((v) => (
                        <td key={`tp${v}`} className="ck-cov-table__num ck-cov-table__num--total">{(totals[`poles_${v}`] || 0).toLocaleString()}</td>
                      ))}
                      <td className="ck-cov-table__num ck-cov-table__num--bold ck-cov-table__num--total ck-cov-table__num--chk">{totals.checkups_total.toLocaleString()}</td>
                      {VOLT_LABELS.map((v) => (
                        <td key={`tc${v}`} className="ck-cov-table__num ck-cov-table__num--total ck-cov-table__num--chk">{(totals[`checkups_${v}`] || 0).toLocaleString()}</td>
                      ))}
                      <td className={"ck-cov-table__num ck-cov-table__num--pct ck-cov-table__num--total " + pctClass(totals.checkups_total, totals.poles_total)}>
                        {pct(totals.checkups_total, totals.poles_total)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
          {data && data.length > 0 && (
            <div className="ck-coverage__export-row">
              <button
                className="ck-coverage__export"
                onClick={handleExport}
                disabled={exporting}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {exporting ? "Юкланмоқда..." : "Excel юклаб олиш"}
              </button>
            </div>
          )}
        </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function CheckupsNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions } = useContext(UserContext);
  const isGlobal = (permissions || []).includes("INCIDENT_CENTRAL_ACTION");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const [page, setPage] = useState(1);
  const [statsOpen, setStatsOpen] = useState(false);

  const [q, setQ] = useState("");
  const [networkQ, setNetworkQ] = useState("");
  const [status, setStatus] = useState("");
  const [isOffline, setIsOffline] = useState("");
  const [checkupType, setCheckupType] = useState("");
  const currentYearRange = getCurrentYearRange();
  const [from, setFrom] = useState(currentYearRange.from);
  const [to, setTo] = useState(currentYearRange.to);
  const [branchId, setBranchId] = useState("");
  const [inspectorId, setInspectorId] = useState(null);
  const [inspectorLabel, setInspectorLabel] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);

  // Meta: cities for branch filter
  const [cities, setCities] = useState([]);
  const itemsRef = useRef([]);
  const listRequestIdRef = useRef(0);
  const activeListKeyRef = useRef("");
  const hasLoadedListRef = useRef(false);
  const lastFilterKeyRef = useRef(null);

  // lastVisit ref — read once on mount so multiple load() calls share the same value
  const lastVisitRef = useRef(null);
  useEffect(() => {
    const str = localStorage.getItem("checkups_last_view");
    lastVisitRef.current = str ? new Date(str) : null;
    localStorage.setItem("checkups_last_view", new Date().toISOString());
  }, []);

  // Inspector autocomplete
  const [inspectorSearch, setInspectorSearch] = useState("");
  const [inspectorResults, setInspectorResults] = useState([]);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const inspectorRef = useRef(null);

  // Gallery
  const [gallery, setGallery] = useState({ open: false, items: [], index: 0 });

  const openGallery = (files, idx) => {
    const list = files.map((f, i) => ({
      id: f.directus_files_id,
      title: `Фото ${i + 1}`,
      thumb: `${DIRECTUS_URL}/assets/${f.directus_files_id}?key=thumb-480`,
      full: `${DIRECTUS_URL}/assets/${f.directus_files_id}`,
    }));
    setGallery({ open: true, items: list, index: idx });
  };

  // Load meta (cities)
  useEffect(() => {
    apiFetch("/api/pole-checkups/meta")
      .then((r) => r.json())
      .then((d) => setCities(d.cities || []))
      .catch(() => {});
  }, []);

  // Load checkups
  const load = useCallback(async (p, filters) => {
    const params = buildCheckupsParams(p, filters);
    const loadKey = params.toString();
    if (activeListKeyRef.current === loadKey) return;

    const requestId = listRequestIdRef.current + 1;
    listRequestIdRef.current = requestId;
    activeListKeyRef.current = loadKey;
    const isCurrentRequest = () => listRequestIdRef.current === requestId;
    const shouldBlockList = !hasLoadedListRef.current && itemsRef.current.length === 0;

    if (shouldBlockList) {
      setIsInitialLoading(true);
    } else {
      setIsFetching(true);
    }
    setError("");
    try {
      const lastVisit = lastVisitRef.current;

      const res = await apiFetch(`/api/pole-checkups?${params}`);
      if (!isCurrentRequest()) return;
      if (res.status === 401) { navigate("/login"); return; }
      if (res.status === 403) { navigate("/no-access"); return; }
      if (!res.ok) throw new Error("Юкланмади");
      const data = await res.json();
      if (!isCurrentRequest()) return;
      const nextItems = (data.items || []).map((item) => ({
        ...item,
        isNew: lastVisit
          ? new Date(item.date_created || item.performed_at) > lastVisit
          : false,
      }));
      itemsRef.current = nextItems;
      hasLoadedListRef.current = true;
      setItems(nextItems);
      setTotal(data.total || 0);
      setExpandedId((prev) => {
        if (!prev) return prev;
        return nextItems.some((item) => getCheckupRowId(item) === prev) ? prev : null;
      });
    } catch (e) {
      if (!isCurrentRequest()) return;
      if (e?.status === 401) { navigate("/login"); return; }
      if (e?.status === 403) { navigate("/no-access"); return; }
      setError(e.message || "Хато");
    } finally {
      if (isCurrentRequest()) {
        setIsInitialLoading(false);
        setIsFetching(false);
        if (activeListKeyRef.current === loadKey) {
          activeListKeyRef.current = "";
        }
      }
    }
  }, [navigate]);

  // Debounce q and networkQ
  const debouncedQ = useDebounce(q, 400);
  const debouncedNetQ = useDebounce(networkQ, 400);
  useEffect(() => {
    const filters = {
      q: debouncedQ,
      networkQ: debouncedNetQ,
      status,
      isOffline,
      checkupType,
      from,
      to,
      branchId,
      inspectorId,
      isEmergency,
    };
    const filterKey = JSON.stringify(filters);

    if (lastFilterKeyRef.current !== null && lastFilterKeyRef.current !== filterKey && page !== 1) {
      lastFilterKeyRef.current = filterKey;
      setPage(1);
      return;
    }

    lastFilterKeyRef.current = filterKey;
    load(page, filters);
  }, [page, debouncedQ, debouncedNetQ, status, isOffline, checkupType, from, to, branchId, inspectorId, isEmergency, load]);

  // Inspector autocomplete
  useEffect(() => {
    if (inspectorSearch.length < 2) { setInspectorResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await apiFetch(`/api/pole-checkups/inspectors?q=${encodeURIComponent(inspectorSearch)}`);
        const data = await res.json();
        setInspectorResults(Array.isArray(data) ? data : []);
        setInspectorOpen(true);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [inspectorSearch]);

  // Close inspector dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (inspectorRef.current && !inspectorRef.current.contains(e.target)) {
        setInspectorOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectInspector = (u) => {
    setInspectorId(u.id);
    setInspectorLabel(safeName(u));
    setInspectorSearch(safeName(u));
    setInspectorOpen(false);
  };

  const clearInspector = () => {
    setInspectorId(null);
    setInspectorLabel("");
    setInspectorSearch("");
  };

  const resetFilters = () => {
    setQ("");
    setNetworkQ("");
    setStatus("");
    setIsOffline("");
    setCheckupType("");
    const yearRange = getCurrentYearRange();
    setFrom(yearRange.from);
    setTo(yearRange.to);
    setBranchId("");
    setIsEmergency(false);
    clearInspector();
    setPage(1);
  };

  const hasFilters = q || networkQ || status || isOffline || checkupType ||
    from !== currentYearRange.from || to !== currentYearRange.to || branchId || inspectorId || isEmergency;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const hasItems = items.length > 0;
  const showInitialState = isInitialLoading && !hasItems;
  const showFetchingIndicator = isFetching && hasItems;
  const showBlockingError = !showInitialState && !isFetching && error && !hasItems;
  const showInlineError = !isFetching && error && hasItems;
  const currentVerified = items.filter(
    (item) => String(item.verification_status || "").toLowerCase() === "verified"
  ).length;
  const currentFailed = items.filter(
    (item) => String(item.verification_status || "").toLowerCase() === "failed"
  ).length;
  const currentOffline = items.filter(
    (item) => item.is_offline === true || item.is_offline === "true"
  ).length;
  const currentEmergency = items.filter(
    (item) => item.is_emergency === true || item.is_emergency === "true"
  ).length;

  return (
    <Layout>
      <div className="container">
      <div className="ck-page">
        {/* Header */}
        <div className="ck-header">
          <div className="ck-header__title-block">
            {/* <span className="ck-header__eyebrow">Кўрик назорати</span> */}
            <h1 className="ck-header__title">Кўриклар реестри</h1>
            <div className="ck-header__subtitle">
              Жами {total.toLocaleString("ru-RU")} та ёзув · {page} бет
            </div>
          </div>
          <button
            className={"ck-header__stats-btn" + (statsOpen ? " ck-header__stats-btn--active" : "")}
            onClick={() => setStatsOpen((p) => !p)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="7" width="4" height="14" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" />
            </svg>
            Қамров статистикаси
          </button>
        </div>

        <div className="ck-summary" aria-label="Жорий саҳифа кўрсаткичлари">
          <div className="ck-summary__item">
            <span>Жорий саҳифа</span>
            <strong>{items.length.toLocaleString("ru-RU")}</strong>
          </div>
          <div className="ck-summary__item ck-summary__item--ok">
            <span>Тасдиқланган</span>
            <strong>{currentVerified}</strong>
          </div>
          <div className="ck-summary__item ck-summary__item--bad">
            <span>Мувофиқ эмас</span>
            <strong>{currentFailed}</strong>
          </div>
          <div className="ck-summary__item">
            <span>Офлайн</span>
            <strong>{currentOffline}</strong>
          </div>
          <div className="ck-summary__item ck-summary__item--warn">
            <span>Авариявий</span>
            <strong>{currentEmergency}</strong>
          </div>
        </div>

        {/* Coverage Stats */}
        <CoverageStats open={statsOpen} from={from} to={to} />

        {/* Filters */}
        <div className="ck-filters">
          <div className="ck-filters__row">
            {/* Network search */}
            <div className="ck-filter ck-filter--grow">
              <svg className="ck-filter__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
              <input
                className="ck-filter__input"
                placeholder="Линия номи бўйича қидириш..."
                value={networkQ}
                onChange={(e) => setNetworkQ(e.target.value)}
              />
              {networkQ && (
                <button className="ck-filter__clear" onClick={() => setNetworkQ("")}>✕</button>
              )}
            </div>

            {/* Status */}
            <select className="ck-filter__select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Барча статуслар</option>
              <option value="verified">Тасдиқланди</option>
              <option value="failed">Мувофиқ эмас</option>
              <option value="pending">Текширилмаган</option>
            </select>

            {/* Offline */}
            <select className="ck-filter__select" value={isOffline} onChange={(e) => setIsOffline(e.target.value)}>
              <option value="">Онлайн ва офлайн</option>
              <option value="false">Фақат онлайн</option>
              <option value="true">Фақат офлайн</option>
            </select>

            <div className="ck-filter ck-filter--grow">
              <svg className="ck-filter__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="ck-filter__input"
                placeholder="Опора номи бўйича қидириш..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q && (
                <button className="ck-filter__clear" onClick={() => setQ("")}>✕</button>
              )}
            </div>

            {/* Type */}
            <select className="ck-filter__select" value={checkupType} onChange={(e) => setCheckupType(e.target.value)}>
              <option value="">Барча турлар</option>
              <option value="full">Тўлиқ кўрик</option>
              <option value="short">Қисман кўрик</option>
            </select>

            {/* Emergency */}
            <label className={"ck-filter-check " + (isEmergency ? "is-active" : "")}>
              <input
                type="checkbox"
                checked={isEmergency}
                onChange={(e) => setIsEmergency(e.target.checked)}
              />
              Авариявий
            </label>
          </div>

          <div className="ck-filters__row">
            {/* Pole search */}
            {/* <div className="ck-filter ck-filter--grow">
              <svg className="ck-filter__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="ck-filter__input"
                placeholder="Опора номи бўйича қидириш..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {q && (
                <button className="ck-filter__clear" onClick={() => setQ("")}>✕</button>
              )}
            </div> */}

            {/* Date from */}
            <div className="ck-filter ck-filter--date">
              <label className="ck-filter__label">Санадан</label>
              <input type="date" className="ck-filter__input ck-filter__input--date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>

            {/* Date to */}
            <div className="ck-filter ck-filter--date">
              <label className="ck-filter__label">Санагача</label>
              <input type="date" className="ck-filter__input ck-filter__input--date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>

            {/* Branch (global only) */}
            {isGlobal && cities.length > 1 && (
              <select className="ck-filter__select" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Барча филиаллар</option>
                {cities.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}

            {/* Inspector autocomplete */}
            <div className="ck-filter ck-filter--autocomplete" ref={inspectorRef}>
              <input
                className="ck-filter__input"
                placeholder="Ходим..."
                value={inspectorSearch}
                onChange={(e) => {
                  setInspectorSearch(e.target.value);
                  if (inspectorId) clearInspector();
                }}
              />
              {inspectorId && (
                <button className="ck-filter__clear" onClick={clearInspector}>✕</button>
              )}
              {inspectorOpen && inspectorResults.length > 0 && (
                <div className="ck-autocomplete">
                  {inspectorResults.map((u) => (
                    <button key={u.id} className="ck-autocomplete__item" onMouseDown={() => selectInspector(u)}>
                      <span className="ck-autocomplete__name">{safeName(u)}</span>
                      {u.department && <span className="ck-autocomplete__dept">{u.department}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reset */}
            {hasFilters && (
              <button className="ck-btn ck-btn--ghost" onClick={resetFilters}>
                Тозалаш
              </button>
            )}
          </div>
        </div>

        {/* Results bar */}
        {/* {!loading && !error && (
          <div className="ck-results-bar">
            <span>{total.toLocaleString("ru-RU")} та кўрик</span>
            {totalPages > 1 && (
              <span className="ck-results-bar__pages">
                {page} / {totalPages} бет
              </span>
            )}
          </div>
        )} */}

        {/* List */}
        <div className={"ck-list" + (showFetchingIndicator ? " ck-list--fetching" : "")}>
          {showFetchingIndicator && (
            <div className="ck-fetching-bar" role="status" aria-live="polite">
              <div className="ck-spinner ck-spinner--sm" />
              Янгиланмоқда...
            </div>
          )}

          {showInitialState && (
            <div className="ck-state">
              <div className="ck-spinner" />
              Юкланмоқда...
            </div>
          )}

          {showBlockingError && (
            <div className="ck-state ck-state--error">{error}</div>
          )}

          {showInlineError && (
            <div className="ck-fetching-bar ck-fetching-bar--error">{error}</div>
          )}

          {!showInitialState && !showBlockingError && !error && items.length === 0 && (
            <div className="ck-state">Кўрик топилмади</div>
          )}

          {!showInitialState && items.map((c) => {
            const rowId = getCheckupRowId(c);

            return (
              <CheckupRow
                key={rowId}
                checkup={c}
                expanded={expandedId === rowId}
                onToggle={() => setExpandedId((prev) => (prev === rowId ? null : rowId))}
                onOpenGallery={openGallery}
              />
            );
          })}
        </div>

        {/* Pagination */}
        {!showInitialState && totalPages > 1 && (
          <div className="ck-pagination">
            <button
              className="ck-pagination__btn"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹
            </button>
            {pageButtons(page, totalPages).map((p, i) =>
              p === "..." ? (
                <span key={`dots-${i}`} className="ck-pagination__dots">…</span>
              ) : (
                <button
                  key={p}
                  className={"ck-pagination__btn" + (p === page ? " is-active" : "")}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              className="ck-pagination__btn"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        )}
      </div>

      </div>
      <SchemeModal
        open={gallery.open}
        items={gallery.items}
        index={gallery.index}
        onClose={() => setGallery((g) => ({ ...g, open: false }))}
        onChangeIndex={(i) => setGallery((g) => ({ ...g, index: i }))}
      />
    </Layout>
  );
}

// ─── Row component ─────────────────────────────────────────────────────────────
function CheckupRow({ checkup: c, expanded, onToggle, onOpenGallery }) {
  const pole = c.pole || {};
  const network = pole.network || {};
  const city = pole.city || {};
  const inspector = safeName(c.inspector_user);
  const inspector2 = safeName(c.inspector_2_user);
  const negReasons = parseNegReasons(c.negative_reasons);
  const yesComments = parseNegReasons(c.yes_comments);
  const issueCount = countIssues(c, c.checkup_type);
  const statusKey = String(c.verification_status || "").toLowerCase();
  const files = Array.isArray(c.files) ? c.files : [];
  const latitude = parseCoordinate(c.latitude, "lat");
  const longitude = parseCoordinate(c.longitude, "lng");

  return (
    <article className={"ck-row" + (expanded ? " ck-row--expanded" : "") + (c.isNew ? " ck-row--new" : "")}>
      <button className="ck-row__head" onClick={onToggle}>
        <div className="ck-row__main">
          <div className="ck-row__title-row">
            <span className="ck-row__pole-name">{pole.name || `#${c.id}`}</span>
            {c.isNew && <span className="ck-badge ck-badge--new">ЯНГИ</span>}
          </div>
          <div className="ck-row__chips">
            {network.name && <span className="ck-chip">{network.name}</span>}
            {city.name && <span className="ck-chip ck-chip--muted">{city.name}</span>}
          </div>
        </div>

        <div className="ck-row__right">
          {inspector && <span className="ck-row__inspector">{inspector}</span>}
          {(c.is_offline === true || c.is_offline === "true") && <span className="ck-badge ck-badge--offline-pill">Офлайн</span>}
          {(c.is_emergency === true || c.is_emergency === "true") && <span className="ck-badge ck-badge--emergency">Авариявий</span>}
          <span className={"ck-badge ck-badge--type"}>
            {isShortCheckup(c.checkup_type) ? "Қисман" : "Тўлиқ"}
          </span>
          {statusKey && (
            <span className={"ck-badge ck-badge--" + (STATUS_MOD[statusKey] || "pending")}>
              {STATUS_LABEL[statusKey] || statusKey}
            </span>
          )}
          {issueCount > 0 && (
            <span className="ck-badge ck-badge--issues">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              {issueCount}
            </span>
          )}
          {files.length > 0 && (
            <span className="ck-badge ck-badge--photo">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              {files.length}
            </span>
          )}
          <span className="ck-row__date">{formatDate(c.performed_at)}</span>
          <svg className={"ck-row__chevron" + (expanded ? " ck-row__chevron--up" : "")}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="ck-row__body">
          {/* Meta grid */}
          <div className="ck-meta-grid">
            <div className="ck-meta-item">
              <span className="ck-meta-item__label">Сана</span>
              <span className="ck-meta-item__value">{formatDate(c.performed_at)}</span>
            </div>
            <div className="ck-meta-item">
              <span className="ck-meta-item__label">Кўрик тури</span>
              <span className="ck-meta-item__value">{isShortCheckup(c.checkup_type) ? "Қисман кўрик" : "Тўлиқ кўрик"}</span>
            </div>
            <div className="ck-meta-item">
              <span className="ck-meta-item__label">Гео статус</span>
              <span className="ck-meta-item__value">{STATUS_LABEL[statusKey] || statusKey || "—"}</span>
            </div>
            {c.verification_reason && (
              <div className="ck-meta-item">
                <span className="ck-meta-item__label">Сабаб</span>
                <span className="ck-meta-item__value">{REASON_LABEL[c.verification_reason] || c.verification_reason}</span>
              </div>
            )}
            {c.distance_to_pole_m != null && (
              <div className="ck-meta-item">
                <span className="ck-meta-item__label">Масофа</span>
                <span className="ck-meta-item__value">{Math.round(Number(c.distance_to_pole_m))} м</span>
              </div>
            )}
            {inspector2 && (
              <div className="ck-meta-item">
                <span className="ck-meta-item__label">2-инспектор</span>
                <span className="ck-meta-item__value">{inspector2}</span>
              </div>
            )}
            {latitude !== null && longitude !== null && (
              <div className="ck-meta-item">
                <span className="ck-meta-item__label">Локация</span>
                <a
                  className="ck-meta-item__value ck-meta-item__value--link"
                  href={`https://map.uzbekistonmet.uz/map?lat=${latitude}&lng=${longitude}&z=17&label=${encodeURIComponent(pole.name || "")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {latitude.toFixed(5)}, {longitude.toFixed(5)}
                </a>
              </div>
            )}
            {c.notes && (
              <div className="ck-meta-item ck-meta-item--wide">
                <span className="ck-meta-item__label">Изоҳ</span>
                <span className="ck-meta-item__value ck-meta-item__value--pre">{c.notes}</span>
              </div>
            )}
          </div>

          {/* Photos */}
          {files.length > 0 && (
            <div className="ck-photos">
              {files.map((f, idx) => (
                <button
                  key={f.id}
                  className="ck-photo"
                  onClick={() => onOpenGallery(files, idx)}
                >
                  <img
                    src={`${DIRECTUS_URL}/assets/${f.directus_files_id}?key=thumb-480`}
                    alt=""
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Check items */}
          <div className="ck-checklist">
            <div className="ck-checklist__title">Кўрик бандлари</div>
            <div className="ck-checklist__grid">
              {getVisibleItems(c.checkup_type).map(([key, label]) => {
                const v = String(c[key] ?? "").toLowerCase();
                const mod = v === "yes" ? "yes" : v === "no" ? "no" : v === "na" ? "na" : "empty";
                const comment = mod === "no" ? negReasons[key] : mod === "yes" ? yesComments[key] : null;

                return (
                  <div key={key} className={"ck-check-item ck-check-item--" + mod}>
                    <span className="ck-check-item__icon">
                      {mod === "yes" && "✓"}
                      {mod === "no" && "✕"}
                      {mod === "na" && "—"}
                      {mod === "empty" && "·"}
                    </span>
                    <div className="ck-check-item__content">
                      <span className="ck-check-item__label">{label}</span>
                      {comment && (
                        <span className="ck-check-item__reason">{String(comment)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
